import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    step: '01',
    icon: 'ri-chat-3-line',
    title: '加入 LINE@ 或留下訊息',
    desc: '透過 LINE@ 傳訊，或填寫下方留言表單，告訴我你的基本狀況——年齡、是否有音樂背景、想學古箏的原因都可以說說。',
    note: '不需要準備答案，說你現在的狀態就好',
  },
  {
    step: '02',
    icon: 'ri-question-answer-line',
    title: '簡短聊聊你的狀況',
    desc: '我會回覆幾個問題，了解你的生活節奏、學習期待，以及目前有沒有樂器。這個過程通常很輕鬆，就像朋友之間的對話。',
    note: '沒有表格、沒有評量，只是聊聊',
  },
  {
    step: '03',
    icon: 'ri-calendar-2-line',
    title: '約一個體驗課時間',
    desc: '確認彼此都覺得可以試試看之後，我們約一個你方便的時間，來上一堂體驗課。體驗課一般 45-60 分鐘，包含初步了解和上手體驗。',
    note: '體驗後再決定要不要繼續，完全沒有壓力',
  },
  {
    step: '04',
    icon: 'ri-heart-line',
    title: '體驗後，一起決定',
    desc: '體驗結束後，你可以感受一下這個學習環境和教學方式是否適合自己。如果覺得對了，我們討論正式的課程安排；如果覺得不合適，也完全沒關係，我很樂意幫你找到更適合的方向。',
    note: '你的感受是最重要的',
  },
];

export default function TrialProcess() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="trial" className="py-20 md:py-28 bg-[#f5f0e8]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-6 h-px bg-[#b08060]"></div>
          <span className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase">How It Works</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-12 mb-14">
          <h2
            className={`font-serif-tc text-[#2c2420] leading-[1.35] transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
          >
            預約體驗課，就是這四步
          </h2>
          <p className={`text-[1rem] text-[#7a6e62] md:mb-1 transition-all duration-700 delay-100 ${visible ? 'opacity-100' : 'opacity-0'}`}>
            輕鬆開始，沒有義務，只要一個願意試試看的心。
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div
              key={s.step}
              className={`relative bg-white rounded-2xl p-6 border border-[#ede5d8] transition-all duration-700`}
              style={{ transitionDelay: `${i * 100 + 200}ms`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
            >
              {/* Step Number */}
              <div className="text-[2.5rem] font-bold text-[#e8d8c4] leading-none mb-5 font-serif-tc select-none">
                {s.step}
              </div>
              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f5f0e8] mb-4">
                <i className={`${s.icon} text-[#b08060] text-xl`}></i>
              </div>
              <h3 className="font-serif-tc text-[#2c2420] text-[1.0625rem] leading-[1.4] mb-3">{s.title}</h3>
              <p className="text-[0.9375rem] leading-[1.7] text-[#7a6e62] mb-4">{s.desc}</p>
              <div className="border-t border-[#ede5d8] pt-3">
                <p className="text-[0.8125rem] text-[#b08060] italic leading-relaxed">{s.note}</p>
              </div>
              {/* Connector */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-5 h-px bg-[#d4c4b0] z-10"></div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a
            href="https://line.me"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#06C755] hover:bg-[#05a848] text-white text-[1rem] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-line-fill text-xl"></i>
            加入 LINE@ 諮詢
          </a>
          <p className="mt-4 text-[0.9375rem] text-[#9a8470]">或往下填寫留言表單，我會盡快回覆</p>
        </div>
      </div>
    </section>
  );
}
